import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../hooks/useAuth';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { LedgerField, LedgerInput } from '../ui';

const validationSchema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup.string().required('Password is required').min(6, 'Password should be at least 6 characters'),
});

type LoginValues = yup.InferType<typeof validationSchema>;

interface LoginFormProps {
  onSuccess?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const { login, error } = useAuth();
  const { settings: publicSettings } = usePublicSettings();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({ resolver: yupResolver(validationSchema), mode: 'onTouched' });

  const onSubmit = async (values: LoginValues) => {
    try {
      await login(values);
      onSuccess?.();
    } catch {
      // The auth slice owns the error state.
    }
  };

  return (
    <div>
      <h2 className="mb-3 font-serif text-scale-3 text-ink">
        Sign in{publicSettings?.SITE_NAME ? ` to ${publicSettings.SITE_NAME}` : ''}
      </h2>
      {error && (
        <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
          {error}
        </p>
      )}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LedgerField label="Email address" htmlFor="email" error={errors.email?.message}>
          <LedgerInput id="email" type="email" autoComplete="email" autoFocus invalid={!!errors.email} {...register('email')} />
        </LedgerField>
        <LedgerField label="Password" htmlFor="password" error={errors.password?.message}>
          <LedgerInput id="password" revealToggle autoComplete="current-password" invalid={!!errors.password} {...register('password')} />
        </LedgerField>
        <button
          type="submit"
          disabled={isSubmitting}
          className="mt-1 w-full cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:opacity-60"
        >
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p className="mt-4 text-center text-scale-1">
        <RouterLink to="/forgot-password" className="text-accent hover:underline">
          Forgot password?
        </RouterLink>
      </p>
      {publicSettings?.ALLOW_PUBLIC_REGISTRATION && (
        <p className="mt-2 text-center text-scale-1 text-ink-muted">
          Don't have an account?{' '}
          <RouterLink to="/register" className="text-accent hover:underline">
            Sign up
          </RouterLink>
        </p>
      )}
    </div>
  );
};
